/**
 * outputFormat 駆動の classifications テーブル + 固定列 fallback (docs/108 AW5)。
 *
 * old2 PostsModerationPage の以下を admin posts-moderation 詳細 modal 用に移植:
 *   - DynamicClassificationsTable (L845-893): outputFormat.fields[] 駆動の動的列テーブル。
 *   - FixedWalkerClassificationsTable (L896-942) / FixedBousaiClassificationsTable (L945-979):
 *     outputFormatId 未設定の旧プリセット用 fallback (#/評価/対象物/想定災害 or GQL観点/コメント・根拠)。
 *
 * `schema` (= AiOutputFormat.schema) が解決できれば DynamicClassificationsTable、
 * できなければ mode に応じた Fixed*Table を描画する (回帰ゼロ)。
 */
import type { AiOutputFormatSchema } from '@citizen-walk/shared-types';
import {
  buildClassificationColumns,
  classificationRowBgClass,
  type AssessmentTone,
} from './classificationTableRenderer';

export type ClassificationKind = 'bousai' | 'walker';

/** 防災 = 緑/橙、ウォーカブル = ピンク/青 (old2 配色)。 */
const TONE: Record<ClassificationKind, AssessmentTone> = {
  bousai: {
    positiveBadge: 'bg-green-600',
    negativeBadge: 'bg-orange-600',
    positiveRow: 'bg-green-50/60',
    negativeRow: 'bg-orange-50/60',
  },
  walker: {
    positiveBadge: 'bg-pink-500',
    negativeBadge: 'bg-blue-500',
    positiveRow: 'bg-pink-50/60',
    negativeRow: 'bg-blue-50/60',
  },
};

export interface ClassificationsTableProps {
  /** 解析 mode (配色 + 固定列の分岐)。 */
  kind: ClassificationKind;
  /** 解決済の outputFormat schema。null なら固定列 fallback。 */
  schema: AiOutputFormatSchema | null;
  /** AI 解析結果の classifications 配列 (各 mode の素の Record 群)。 */
  classifications: Record<string, unknown>[];
}

/**
 * schema があれば動的列、無ければ mode 別固定列で classifications を描画する。
 */
export function ClassificationsTable({
  kind,
  schema,
  classifications,
}: ClassificationsTableProps): React.ReactElement | null {
  if (classifications.length === 0) return null;
  const tone = TONE[kind];
  const columns = schema ? buildClassificationColumns(schema, tone) : null;

  if (schema && columns) {
    return (
      <div>
        <p className="text-xs text-slate-500 mb-1">
          分類セット（{classifications.length}件／重要度順）
        </p>
        <div className="overflow-x-auto -mx-1" data-testid="classifications-table">
          <table className="min-w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className="text-left px-1.5 py-1 border border-slate-200"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {classifications.map((cls, i) => (
                <tr key={i} className={classificationRowBgClass(schema, cls, tone)}>
                  {columns.map((col) => (
                    <td key={col.key} className="align-top px-1.5 py-1 border border-slate-200">
                      {col.render(cls, i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return kind === 'walker' ? (
    <FixedWalkerClassificationsTable classifications={classifications} />
  ) : (
    <FixedBousaiClassificationsTable classifications={classifications} />
  );
}

/** outputFormatId 未設定の旧プリセット用 fallback (walker)。old2 L896-942 踏襲。 */
function FixedWalkerClassificationsTable({
  classifications,
}: {
  classifications: Record<string, unknown>[];
}) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1">
        分類セット（{classifications.length}件／重要度順）
      </p>
      <div className="overflow-x-auto -mx-1">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="px-1.5 py-1 border border-slate-200 text-left">#</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">評価</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">対象物</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">GQL観点</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">根拠</th>
            </tr>
          </thead>
          <tbody>
            {classifications.map((c, i) => {
              const assessment = String(c.assessment ?? '');
              const isPositive = assessment === 'いいね！';
              return (
                <tr key={i} className={isPositive ? 'bg-pink-50/60' : 'bg-blue-50/60'}>
                  <td className="px-1.5 py-1 border border-slate-200 font-mono">
                    {String(c.rank ?? '')}
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200">
                    <span
                      className={`px-1.5 py-0.5 rounded text-white font-semibold ${isPositive ? 'bg-pink-500' : 'bg-blue-500'}`}
                    >
                      {assessment}
                      {String(c.assessment_level ?? '')}({String(c.score ?? '')})
                    </span>
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200">
                    <div className="font-mono text-[10px] text-slate-500">
                      {String(c.category_code ?? '')}
                    </div>
                    <div>{String(c.category_name ?? '')}</div>
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200">
                    <div className="font-mono text-[10px] text-slate-500">
                      {String(c.gql_code ?? '')}
                    </div>
                    <div>{String(c.gql_name ?? '')}</div>
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200 text-slate-600 max-w-xs">
                    <span className="whitespace-pre-wrap">{String(c.reasoning ?? '')}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** outputFormatId 未設定の旧プリセット用 fallback (bousai)。old2 L945-979 踏襲。 */
function FixedBousaiClassificationsTable({
  classifications,
}: {
  classifications: Record<string, unknown>[];
}) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1">分類セット（{classifications.length}件）</p>
      <div className="overflow-x-auto -mx-1">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="px-1.5 py-1 border border-slate-200 text-left">#</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">評価</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">対象物</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">想定災害</th>
              <th className="px-1.5 py-1 border border-slate-200 text-left">コメント</th>
            </tr>
          </thead>
          <tbody>
            {classifications.map((c, i) => {
              const assessment = String(c.assessment ?? '');
              return (
                <tr
                  key={i}
                  className={
                    assessment === 'ホッと'
                      ? 'bg-green-50/60'
                      : assessment === 'ハッと'
                        ? 'bg-orange-50/60'
                        : ''
                  }
                >
                  <td className="px-1.5 py-1 border border-slate-200 font-mono">
                    {String(c.rank ?? '')}
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200">
                    <span
                      className={`px-1.5 py-0.5 rounded text-white font-semibold ${assessment === 'ホッと' ? 'bg-green-600' : 'bg-orange-600'}`}
                    >
                      {assessment}
                      {String(c.assessment_level ?? '')}({String(c.score ?? '')})
                    </span>
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200">
                    {String(c.category_name ?? '')}
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200">
                    {String(c.hzr_name ?? '')}
                  </td>
                  <td className="px-1.5 py-1 border border-slate-200 text-slate-600">
                    {String(c.comment ?? '') || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
